import * as React from 'react';
import Switch, { type SwitchProps } from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

export interface ToggleProps extends Omit<SwitchProps, 'onChange' | 'checked' | 'color'> {
  checked: boolean;
  /** react-switch argument order: (checked, event). MUI's is (event, checked); this flips it. */
  onChange: (checked: boolean, event: React.ChangeEvent<HTMLInputElement>) => void;
  color?: SwitchProps['color'];
  /** 'error' paints the unchecked track red, as the Active/Inactive status toggles did. */
  offColor?: 'default' | 'error';
  label?: React.ReactNode;
  labelPlacement?: 'start' | 'end';
}

/** MUI Switch with react-switch's callback signature, so call sites convert by import swap. */
export function Toggle({
  checked,
  onChange,
  color = 'success',
  offColor = 'default',
  label,
  labelPlacement = 'end',
  disabled,
  sx,
  ...rest
}: ToggleProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, next: boolean) => {
    if (disabled) return;
    onChange(next, event);
  };

  const control = (
    <Switch
      checked={Boolean(checked)}
      onChange={handleChange}
      color={color}
      disabled={disabled}
      sx={[
        offColor === 'error' && {
          '& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track': {
            bgcolor: 'error.main',
            opacity: 0.5,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    />
  );

  if (label === undefined) return control;
  return <FormControlLabel control={control} label={label} labelPlacement={labelPlacement} sx={{ m: 0, gap: 1 }} />;
}
